import { Box, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer } from '@tiles-erp/ui';
import type { SixOrbitPendingJobItem, SixOrbitPendingJobState } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { useSixOrbitPendingJobs } from './api';

const stateLabels: Record<SixOrbitPendingJobState, string> = {
  waiting: 'Waiting',
  active: 'Processing',
  delayed: 'Retry delayed',
};

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 150, flex: 1 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" color={color}>{value.toLocaleString()}</Typography>
      </CardContent>
    </Card>
  );
}

export function PendingJobsPage(): JSX.Element {
  const pagination = usePagination();
  const [state, setState] = useState<SixOrbitPendingJobState>('waiting');
  const query = useMemo(() => ({
    page: pagination.query.page,
    pageSize: pagination.query.pageSize,
    search: pagination.query.search,
    state,
  }), [pagination.query, state]);
  const { data, isFetching } = useSixOrbitPendingJobs(query);

  const columns = useMemo<ColDef<SixOrbitPendingJobItem>[]>(() => [
    { field: 'queuePosition', headerName: 'Queue no.', maxWidth: 110 },
    { field: 'sku', headerName: 'SKU', minWidth: 120 },
    { field: 'productName', headerName: 'Product', minWidth: 260 },
    { field: 'sixorbitId', headerName: 'SixOrbit variation ID', minWidth: 170 },
    {
      field: 'state', headerName: 'Status', maxWidth: 130,
      cellRenderer: (params: ICellRendererParams<SixOrbitPendingJobItem>) => (
        <Chip size="small" label={stateLabels[params.data?.state ?? 'waiting']}
          color={params.data?.state === 'active' ? 'primary' : params.data?.state === 'delayed' ? 'warning' : 'default'} />
      ),
    },
    {
      headerName: 'Attempts', maxWidth: 105,
      valueGetter: (params) => `${params.data?.attemptsMade ?? 0} / ${params.data?.maxAttempts ?? 1}`,
    },
    {
      field: 'queuedAt', headerName: 'Queued at', minWidth: 175,
      valueFormatter: (params) => params.value ? new Date(params.value as string).toLocaleString() : '—',
    },
    {
      field: 'processedAt', headerName: 'Started at', minWidth: 175,
      valueFormatter: (params) => params.value ? new Date(params.value as string).toLocaleString() : '—',
    },
    { field: 'id', headerName: 'Job ID', minWidth: 150 },
  ], []);

  const changeState = (next: SixOrbitPendingJobState) => {
    setState(next);
    pagination.setPage(1);
  };

  return (
    <PageContainer title="SixOrbit pending jobs"
      subtitle="Live product pushes waiting for or currently being processed by SixOrbit.">
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <SummaryCard label="Total pending" value={data?.summary.total ?? 0} />
          <SummaryCard label="Waiting" value={data?.summary.waiting ?? 0} />
          <SummaryCard label="Processing" value={data?.summary.active ?? 0} color="primary.main" />
          <SummaryCard label="Retry delayed" value={data?.summary.delayed ?? 0} color="warning.main" />
        </Stack>
        <Box sx={{ width: { xs: '100%', sm: 220 } }}>
          <TextField select fullWidth size="small" label="Job status" value={state}
            onChange={(event) => changeState(event.target.value as SixOrbitPendingJobState)}>
            <MenuItem value="waiting">Waiting</MenuItem>
            <MenuItem value="active">Processing</MenuItem>
            <MenuItem value="delayed">Retry delayed</MenuItem>
          </TextField>
        </Box>
        <DataTable rows={data?.items ?? []} columns={columns} meta={data?.meta}
          pagination={pagination} loading={isFetching}
          searchPlaceholder="Search by SKU, product or SixOrbit ID…"
          exportTitle="SixOrbit pending jobs" exportFilename="sixorbit-pending-jobs" />
      </Stack>
    </PageContainer>
  );
}
